/**
 * useClienteHospitais - Hook for managing cliente hospitals
 *
 * Handles: Hospital list, vincular/desvincular, edit codes
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import clienteService, { Cliente } from '../services/clienteService';
import type { ClienteHospital } from '../types/clienteHospital';
import { msg, MESSAGES } from '../utils/messages';

export interface UseClienteHospitaisConfig {
  selectedCliente: Cliente | null;
  activeTab: string;
}

export interface UseClienteHospitaisReturn {
  hospitais: ClienteHospital[];
  loadingHospitais: boolean;
  searchHospital: string;
  setSearchHospital: (term: string) => void;
  filteredHospitais: ClienteHospital[];
  loadHospitais: (silencioso?: boolean) => Promise<void>;

  // Edit codes
  isEditingCodes: boolean;
  editedCodes: Record<number, string>;
  savingCodes: boolean;
  handleToggleEditMode: () => void;
  handleCodeChange: (hospitalId: number, value: string) => void;
  handleSaveCodes: () => Promise<void>;

  // Operations
  handleDesvincularHospital: (hospitalId: number, confirmFn: (opts: any) => Promise<boolean>) => Promise<void>;
}

export const useClienteHospitais = ({
  selectedCliente,
  activeTab,
}: UseClienteHospitaisConfig): UseClienteHospitaisReturn => {
  const [hospitais, setHospitais] = useState<ClienteHospital[]>([]);
  const [loadingHospitais, setLoadingHospitais] = useState(false);
  const [searchHospital, setSearchHospital] = useState('');

  // Edit codes state
  const [isEditingCodes, setIsEditingCodes] = useState(false);
  const [editedCodes, setEditedCodes] = useState<Record<number, string>>({});
  const [savingCodes, setSavingCodes] = useState(false);

  // N232: só vale a recarga mais recente do cliente atual. Nem a resposta atrasada
  // de outro cliente, nem a recarga que um handler do cliente anterior dispara
  // depois do seu await, podem cobrir a lista: com hospitais do cliente errado na
  // tela, "Salvar códigos" criaria vínculos no cliente errado.
  const clienteAtual = useRef<number | null>(null);
  const ultimaRecarga = useRef(0);

  // Load hospitais. `silencioso` recarrega sem trocar a tabela pelo spinner e sem
  // descartar códigos em edição: depois de vincular locais, a lista continua onde
  // o usuário estava (N232).
  const loadHospitais = useCallback(async (silencioso = false) => {
    if (!selectedCliente || selectedCliente.id !== clienteAtual.current) return;
    const recarga = ++ultimaRecarga.current;

    if (!silencioso) setLoadingHospitais(true);
    try {
      const data = await clienteService.getClienteHospitais(selectedCliente.id);
      if (recarga !== ultimaRecarga.current) return;
      setHospitais(data);
      if (!silencioso) {
        // Reset edit mode on reload
        setIsEditingCodes(false);
        setEditedCodes({});
      }
    } catch (err: any) {
      if (recarga !== ultimaRecarga.current) return;
      console.error('Erro ao carregar hospitais:', err);
      msg.error(MESSAGES.CLIENT.HOSPITAL_LOAD_ERROR);
    } finally {
      if (recarga === ultimaRecarga.current) setLoadingHospitais(false);
    }
  }, [selectedCliente]);

  // Load when cliente changes (always load to show count in header). Trocando de
  // cliente, a lista do anterior sai na hora: cliente novo + lista velha foi a
  // causa do N232.
  useEffect(() => {
    const id = selectedCliente?.id ?? null;
    if (id !== clienteAtual.current) setHospitais([]);
    clienteAtual.current = id;
    if (selectedCliente) {
      loadHospitais();
    }
  }, [selectedCliente, loadHospitais]);

  // Toggle edit mode
  const handleToggleEditMode = useCallback(() => {
    if (!isEditingCodes) {
      // Enter edit mode: copy current values
      const initialCodes: Record<number, string> = {};
      hospitais.forEach(h => {
        initialCodes[h.hospital_id] = h.codigo || '';
      });
      setEditedCodes(initialCodes);
      setIsEditingCodes(true);
    } else {
      // Cancel edit mode
      setIsEditingCodes(false);
      setEditedCodes({});
    }
  }, [isEditingCodes, hospitais]);

  // Handle code change
  const handleCodeChange = useCallback((hospitalId: number, value: string) => {
    setEditedCodes(prev => ({
      ...prev,
      [hospitalId]: value
    }));
  }, []);

  // Save codes
  const handleSaveCodes = useCallback(async () => {
    if (!selectedCliente) return;

    // Identify which changed
    const updates = hospitais.filter(h => {
      const newValue = editedCodes[h.hospital_id];
      return newValue !== undefined && newValue !== (h.codigo || '');
    });

    if (updates.length === 0) {
      setIsEditingCodes(false);
      return;
    }

    try {
      setSavingCodes(true);

      // Execute updates in parallel
      const promises = updates.map(h => {
        const newCode = editedCodes[h.hospital_id];
        const dataInicio = h.data_inicio.split('T')[0];

        return clienteService.vincularHospitalSingle(
          selectedCliente.id,
          h.hospital_id,
          newCode,
          dataInicio
        );
      });

      await Promise.all(promises);

      msg.success(`${updates.length} código(s) atualizado(s) com sucesso`);
      await loadHospitais();
    } catch (err: any) {
      console.error('Erro ao salvar códigos:', err);
      msg.error('Erro ao salvar alterações nos códigos');
    } finally {
      setSavingCodes(false);
    }
  }, [selectedCliente, hospitais, editedCodes, loadHospitais]);

  // Desvincular hospital
  const handleDesvincularHospital = useCallback(async (
    hospitalId: number,
    confirmFn: (opts: any) => Promise<boolean>
  ) => {
    if (!selectedCliente) return;

    const confirmed = await confirmFn({
      title: 'Desvincular Hospital',
      message: MESSAGES.CLIENT.HOSPITAL_CONFIRM_UNLINK,
      variant: 'warning',
      confirmText: 'Desvincular',
    });

    if (!confirmed) return;

    try {
      await clienteService.desvincularHospital(selectedCliente.id, hospitalId);
      await loadHospitais();
    } catch (err: any) {
      console.error('Erro ao desvincular hospital:', err);
      msg.error(MESSAGES.CLIENT.HOSPITAL_UNLINK_ERROR);
    }
  }, [selectedCliente, loadHospitais]);

  // Filtered hospitais
  const filteredHospitais = hospitais.filter(ch =>
    ch.hospital_nome?.toLowerCase().includes(searchHospital.toLowerCase()) ||
    ch.hospital_codigo?.toLowerCase().includes(searchHospital.toLowerCase()) ||
    ch.hospital_cidade?.toLowerCase().includes(searchHospital.toLowerCase())
  );

  return {
    hospitais,
    loadingHospitais,
    searchHospital,
    setSearchHospital,
    filteredHospitais,
    loadHospitais,
    isEditingCodes,
    editedCodes,
    savingCodes,
    handleToggleEditMode,
    handleCodeChange,
    handleSaveCodes,
    handleDesvincularHospital,
  };
};

export type { ClienteHospital };
